/* ============================================================
   CarrierPickupMapPanel — 63b51a7e: carrier ПВЗ pick at cart
   checkout on a Yandex Maps 3.0 map, with a city selector
   (catalog clusters + IP geolocation) and carrier filter chips.
   Clean degrade: no VITE_YMAPS_API_KEY (local dev, tests) or a
   script failure → the same selection as a plain list.

   2026-09-03 (Артем): full map-first rework —
   • every service on one map: Ozon + СДЭК + Почта России from the
     catalog (per-carrier windows so a small feed is never cut by a
     dominant one) and the performer's own pickup points as an
     orange «Самовывоз» layer;
   • brand colors per service; nearby points merge into count
     bubbles («3» when three ПВЗ collapse together);
   • a point card with address/schedule and a live tariff quote
     (parcel mass/dims from the server's calculation + packaging
     reserve) — the table stays only as the no-map fallback;
   • the map renders only after a city resolves (IP auto-pick) and
     the camera holds the CITY center — picking a point never
     moves it.
   ============================================================ */

import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import type {
  CarrierPointSelection,
  CartDeliveryQuote,
  CatalogDeliveryPoint,
  DeliveryPointCity,
} from '../types/delivery';
import type { PublicPickupPoint } from '../types/product';
import {
  carrierColor,
  carrierLabel,
  carrierOptions,
  citySuggestions,
  clusterPoints,
  filterPoints,
  mapCenter,
  pointLabel,
  sameCity,
  toSelection,
  ymapsFailureHint,
  ymapsScriptUrl,
  type CarrierFilter,
} from './carrierPickupView';

declare global {
  interface Window {
    ymaps3?: any; // CDN-loaded Yandex Maps JS API 3.0 (typed boundary)
  }
}

/** Module-scope loader cache — one script load per page per key. */
let ymapsLoader: Promise<any> | null = null;

function loadYmaps(apiKey: string, signature?: string): Promise<any> {
  if (!ymapsLoader) {
    ymapsLoader = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = ymapsScriptUrl(apiKey, signature)!;
      script.async = true;
      script.onload = () => {
        // 2026-09-02 live-loader contract check: in JS API v3 `ymaps3.ready`
        // is a PROMISE, not a callable — calling it threw a silent TypeError
        // and the map promise never settled.
        try {
          const y3 = window.ymaps3;
          const ready = y3?.ready;
          if (!ready) {
            reject(new Error('ymaps3 missing'));
            return;
          }
          Promise.resolve(typeof ready === 'function' ? ready() : ready)
            .then(() => resolve(y3))
            .catch(reject);
        } catch (err) {
          reject(err);
        }
      };
      script.onerror = () => reject(new Error('ymaps script failed'));
      document.head.appendChild(script);
    }).catch((err) => {
      ymapsLoader = null; // a later open may retry (e.g. transient network)
      throw err;
    });
  }
  return ymapsLoader;
}

/** 2026-09-03: per-carrier city windows — a small feed (Почта России pilot
 * cities, СДЭК later) is never cut out by Ozon's thousands inside one
 * mixed window. Overview (no city yet) stays a bounded all-carrier sample. */
const CARRIER_WINDOWS: { carrier: 'ozon' | 'cdek' | 'russianpost'; cap: number }[] = [
  { carrier: 'ozon', cap: 1500 },
  { carrier: 'russianpost', cap: 400 },
  { carrier: 'cdek', cap: 400 },
];
const OVERVIEW_POINTS = 300;
const FETCH_PAGE = 200;
const CITY_ZOOM = 10;
const MAX_ZOOM = 17;

function markerDom(label: string, color: string, active: boolean, onClick: () => void): HTMLButtonElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.textContent = label;
  el.onclick = onClick;
  Object.assign(el.style, {
    padding: '2px 8px',
    borderRadius: '999px',
    border: active ? '2px solid #111827' : '1px solid rgba(255,255,255,0.9)',
    background: color,
    color: '#fff',
    fontWeight: 'var(--fw-semibold, 600)',
    fontSize: 'var(--fs-caption, 12px)',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
  } satisfies Partial<CSSStyleDeclaration>);
  return el;
}

/** Count bubble for merged points («3» = three ПВЗ collapsed together). */
function clusterDom(count: number, color: string, onClick: () => void): HTMLButtonElement {
  const size = 26 + Math.min(14, count);
  const el = document.createElement('button');
  el.type = 'button';
  el.textContent = String(count);
  el.onclick = onClick;
  Object.assign(el.style, {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.9)',
    background: color,
    color: '#fff',
    fontWeight: '700',
    fontSize: count > 99 ? '10px' : '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
  } satisfies Partial<CSSStyleDeclaration>);
  return el;
}

interface Props {
  selected: CarrierPointSelection | null;
  onSelect: (selection: CarrierPointSelection | null) => void;
  /** 2026-09-03 (Артем): performer self-pickup points (single-performer
   * cart) — orange layer on the map; picking one switches the checkout to
   * Самовывоз with that point. */
  pickupPoints?: PublicPickupPoint[] | null;
  selectedPickupId?: string | null;
  onPickupSelect?: (pickupPointId: string | null) => void;
  onCityChange?: (city: string) => void;
}

export function CarrierPickupMapPanel({
  selected,
  onSelect,
  pickupPoints = null,
  selectedPickupId = null,
  onPickupSelect,
  onCityChange,
}: Props) {
  const [points, setPoints] = useState<CatalogDeliveryPoint[] | null>(null);
  const [cities, setCities] = useState<DeliveryPointCity[]>([]);
  const [city, setCity] = useState('');
  const [carrier, setCarrier] = useState<CarrierFilter>('all');
  const [mapState, setMapState] = useState<'idle' | 'ready' | 'failed'>('idle');
  const [zoom, setZoom] = useState(CITY_ZOOM);
  const mapRef = useRef<HTMLDivElement | null>(null);
  const map = useRef<any>(null);

  // 2026-09-03: live tariff quote for the picked carrier point.
  const [quote, setQuote] = useState<CartDeliveryQuote | null>(null);
  const [quoteBusy, setQuoteBusy] = useState(false);

  const apiKey = import.meta.env.VITE_YMAPS_API_KEY;
  const ymapsSign = import.meta.env.VITE_YMAPS_SIGN;
  const mapAvailable = ymapsScriptUrl(apiKey, ymapsSign) !== null;
  const cityKey = city.trim().toLowerCase();
  // Артем: не выводить карту, пока город не выбран автоматически/вручную.
  const cityReady = city.trim().length > 0;

  /** The performer's pickup points mapped into the catalog point shape
   * (carrier 'pickup') so filtering/clustering/card share one pipeline. */
  const pickupAsPoints = useMemo(() => (pickupPoints ?? [])
    .filter((p) => p.geoLat && p.geoLon)
    .map((p): CatalogDeliveryPoint => ({
      carrier: 'pickup',
      externalId: p.pickupPointId,
      externalNumber: null,
      pointType: 'Самовывоз',
      address: p.address,
      city: p.city ?? null,
      geoLat: p.geoLat ?? null,
      geoLon: p.geoLon ?? null,
      workingHours: p.workingHours,
      maxWeightG: null,
      storagePeriodDays: null,
    })), [pickupPoints]);

  // Per-carrier city windows (debounced typing). No city → bounded overview.
  useEffect(() => {
    let cancelled = false;
    setPoints(null);
    const timer = setTimeout(() => {
      (async () => {
        try {
          const trimmed = city.trim();
          if (!trimmed) {
            const collected: CatalogDeliveryPoint[] = [];
            for (let offset = 0; offset < OVERVIEW_POINTS; offset += FETCH_PAGE) {
              const page = await api.getDeliveryPoints(null, offset, FETCH_PAGE, null);
              collected.push(...page.points);
              if (collected.length >= page.total || page.points.length === 0) break;
            }
            if (!cancelled) setPoints(collected);
            return;
          }
          const collected: CatalogDeliveryPoint[] = [];
          await Promise.all(CARRIER_WINDOWS.map(async ({ carrier: c, cap }) => {
            const part: CatalogDeliveryPoint[] = [];
            for (let offset = 0; offset < cap; offset += FETCH_PAGE) {
              const page = await api.getDeliveryPoints(c, offset, FETCH_PAGE, trimmed);
              part.push(...page.points);
              if (part.length >= page.total || page.points.length === 0) break;
            }
            collected.push(...part);
          }));
          if (!cancelled) setPoints(collected);
        } catch {
          if (!cancelled) setPoints([]);
        }
      })();
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cityKey]);

  // City selector clusters (server-side, cached) — loaded once per mount.
  useEffect(() => {
    api
      .getDeliveryPointCities()
      .then((dto) => setCities(dto.cities))
      .catch(() => undefined);
  }, []);

  // IP geolocation → initial city: adopted only when the catalog has it.
  useEffect(() => {
    if (points === null || city) return;
    let cancelled = false;
    api
      .getIpCity()
      .then((dto) => {
        if (cancelled || !dto.city) return;
        const known = cities.some((c) => sameCity(c.city, dto.city));
        if (known) setCity(dto.city);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [points, cities, city]);

  useEffect(() => {
    onCityChange?.(city);
  }, [city, onCityChange]);

  const suggestions = useMemo(
    () => citySuggestions(cities, points, 50, cityKey),
    [cities, points, cityKey],
  );
  const merged = useMemo(
    () => [...(points ?? []), ...pickupAsPoints],
    [points, pickupAsPoints],
  );
  const carriers = useMemo(() => carrierOptions(merged), [merged]);
  const filtered = useMemo(() => filterPoints(merged, city, carrier), [merged, city, carrier]);
  /** CITY center from ALL city points (not the carrier-filtered subset) —
   * the camera holds this; picking a point never moves it (Артем). */
  const cityCenter = useMemo(
    () => mapCenter(filterPoints(merged, city, 'all')),
    [merged, city],
  );

  // Tariff quote for the picked carrier point (any service; самовывоз is
  // free — no request). Parcel mass/dims/value come from the server.
  useEffect(() => {
    if (!selected) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    setQuote(null);
    setQuoteBusy(true);
    api
      .cartDeliveryQuote(selected.carrier, selected.externalId)
      .then((q) => {
        if (!cancelled) setQuote(q);
      })
      .catch((err) => {
        if (!cancelled)
          setQuote({
            success: false,
            carrier: selected.carrier,
            priceRub: null,
            minDays: null,
            maxDays: null,
            transient: false,
            error: err instanceof Error ? err.message : 'Не удалось рассчитать тариф',
            weightGrams: 0,
            lengthCm: 0,
            widthCm: 0,
            heightCm: 0,
            declaredValueRub: 0,
          });
      })
      .finally(() => {
        if (!cancelled) setQuoteBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected?.carrier, selected?.externalId]);

  /** The point the card describes: the picked carrier point or the picked
   * performer pickup point. */
  const selectedPoint = useMemo(() => {
    if (selected)
      return (
        merged.find(
          (p) => p.carrier === selected.carrier && p.externalId === selected.externalId,
        ) ?? null
      );
    if (selectedPickupId)
      return merged.find((p) => p.carrier === 'pickup' && p.externalId === selectedPickupId) ?? null;
    return null;
  }, [selected, selectedPickupId, merged]);

  // Map lifecycle: create once when a city is ready, re-render clustered
  // markers on filter/zoom changes, destroy on unmount. Any failure
  // degrades to the list surface.
  useEffect(() => {
    if (!mapAvailable || mapState === 'failed' || !mapRef.current || !cityReady) return;
    let disposed = false;
    let markerHost: any[] | null = null;

    const pick = (point: CatalogDeliveryPoint) => {
      if (point.carrier === 'pickup') onPickupSelect?.(point.externalId);
      else onSelect(toSelection(point));
    };
    const isActive = (p: CatalogDeliveryPoint) =>
      (selected && p.carrier === selected.carrier && p.externalId === selected.externalId)
      || (p.carrier === 'pickup' && !!selectedPickupId && p.externalId === selectedPickupId);

    (async () => {
      let ymaps: any;
      try {
        ymaps = await loadYmaps(apiKey!, ymapsSign);
      } catch (err) {
        console.warn('[ymaps]', ymapsFailureHint(err));
        if (!disposed) setMapState('failed');
        return;
      }
      if (disposed || !mapRef.current) return;

      if (!map.current) {
        try {
          map.current = new ymaps.YMap(
            mapRef.current,
            {
              location: {
                center: [cityCenter.lon, cityCenter.lat],
                zoom: CITY_ZOOM,
              },
            },
            [new ymaps.YMapDefaultSchemeLayer({}), new ymaps.YMapDefaultFeaturesLayer({})],
          );
        } catch {
          if (!disposed) setMapState('failed');
          return;
        }
      }
      if (!disposed) setMapState('ready');

      try {
        removeMarkerHost();
        // 2026-09-03 (Артем): nearby points merge into count bubbles;
        // a single point renders as its service-colored pill.
        const clusters = clusterPoints(filtered, zoom);
        markerHost = clusters.map((cluster) => {
          const zoomIn = () => {
            const next = Math.min(MAX_ZOOM, zoom + 2);
            setZoom(next);
            try {
              map.current?.setLocation?.({
                center: [cluster.lon, cluster.lat],
                zoom: next,
                duration: 250,
              });
            } catch {
              // best-effort zoom
            }
          };
          if (cluster.count === 1) {
            const point = cluster.points[0]!;
            return new ymaps.YMapMarker(
              { coordinates: [cluster.lon, cluster.lat], onClick: () => pick(point) },
              markerDom(shortLabel(point.carrier), carrierColor(point.carrier), isActive(point), () => pick(point)),
            );
          }
          return new ymaps.YMapMarker(
            { coordinates: [cluster.lon, cluster.lat], onClick: zoomIn },
            clusterDom(
              cluster.count,
              cluster.uniform ? carrierColor(cluster.carrier) : '#1F2937',
              zoomIn,
            ),
          );
        });
        for (const marker of markerHost) map.current.addChild(marker);
      } catch {
        // Marker re-render failed — the fallback list stays the source of truth.
      }
    })();

    return () => {
      disposed = true;
      removeMarkerHost();
    };
    function removeMarkerHost() {
      if (!markerHost) return;
      try {
        for (const marker of markerHost) map.current?.removeChild(marker);
      } catch {
        // map already destroyed
      }
      markerHost = null;
    }
    function shortLabel(carrier: string) {
      return carrier === 'ozon'
        ? 'Ozon'
        : carrier === 'cdek'
          ? 'СДЭК'
          : carrier === 'russianpost'
            ? 'Почта'
            : carrier === 'pickup'
              ? 'Вывоз'
              : carrier;
    }
  }, [mapAvailable, mapState, filtered, zoom, selected, selectedPickupId, onSelect, onPickupSelect, apiKey, cityReady]);

  // Camera holds the CITY center: recenters on city change only — picking a
  // point or switching chips never moves it (Артем).
  useEffect(() => {
    if (!cityReady || mapState !== 'ready') return;
    try {
      map.current?.setLocation?.({
        center: [cityCenter.lon, cityCenter.lat],
        zoom: CITY_ZOOM,
        duration: 300,
      });
    } catch {
      // map not ready yet
    }
    setZoom(CITY_ZOOM);
  }, [cityKey, cityCenter, mapState]);

  // Destroy the map on unmount only.
  useEffect(
    () => () => {
      if (map.current) {
        try {
          map.current.destroy?.();
        } catch {
          // best-effort teardown
        }
        map.current = null;
      }
    },
    [],
  );

  const showMap = mapAvailable && mapState !== 'failed';
  const showFallbackList = !showMap;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-xs)', fontSize: 'var(--fs-caption)' }}>
      <div>
        <span style={{ fontWeight: 'var(--fw-semibold)' }}>Пункт выдачи на карте</span>
      </div>

      {/* City selector: catalog clusters + IP prefill; the map opens only
          after a city resolves (Артем 2026-09-03). */}
      <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          list="carrier-pickup-cities"
          placeholder="Город"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          style={{
            height: 36,
            padding: '0 var(--space-sm)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-separator)',
            background: 'var(--color-surface)',
            color: 'inherit',
            minWidth: 180,
          }}
        />
        <datalist id="carrier-pickup-cities">
          {suggestions.map((c) => (
            <option key={c.city} value={c.city}>{`${c.city} · ${c.count}`}</option>
          ))}
        </datalist>
        {city && (
          <button type="button" className="btn btn-tertiary" style={{ fontSize: 'var(--fs-caption)' }} onClick={() => setCity('')}>
            Все города
          </button>
        )}
        <div style={{ display: 'flex', gap: 'var(--space-2xs)', flexWrap: 'wrap' }}>
          {carriers.map((c) => (
            <button
              key={c.carrier}
              type="button"
              aria-pressed={carrier === c.carrier}
              onClick={() => setCarrier(c.carrier)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '2px 10px',
                borderRadius: 'var(--radius-md)',
                border: carrier === c.carrier
                  ? '2px solid var(--color-primary)'
                  : '1px solid var(--color-separator)',
                background: 'var(--color-surface)',
                color: carrier === c.carrier ? 'var(--color-primary)' : 'var(--color-text-primary)',
                fontWeight: carrier === c.carrier ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                cursor: 'pointer',
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: carrierColor(c.carrier === 'all' ? 'ozon' : c.carrier),
                }}
              />
              {c.label} · {c.count}
            </button>
          ))}
        </div>
      </div>

      {/* The map renders only after a city resolved; until then a quiet
          placeholder keeps the layout stable (Артем 2026-09-03). */}
      {showMap && !cityReady && (
        <div
          style={{
            height: 340,
            borderRadius: 'var(--radius-lg)',
            border: '1px dashed var(--color-separator)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-text-secondary)',
            background: 'var(--color-surface-secondary, var(--color-surface))',
          }}
        >
          Определяем город…
        </div>
      )}
      {showMap && cityReady && (
        <div style={{ position: 'relative' }}>
          <div
            ref={mapRef}
            style={{
              width: '100%',
              height: 340,
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--color-separator)',
              background: 'var(--color-surface-secondary, var(--color-surface))',
            }}
          />
        </div>
      )}
      {!showMap && (
        <p style={{ color: 'var(--color-text-secondary)' }}>
          Карта недоступна — выбираем списком.
        </p>
      )}

      {/* Point card: address/schedule/extras + live tariff quote (Артем). */}
      {selectedPoint && (
        <div
          className="card"
          style={{
            padding: 'var(--space-sm) var(--space-md)',
            display: 'grid',
            gap: 'var(--space-2xs)',
            border: '1px solid var(--color-separator)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
            <span
              aria-hidden
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: carrierColor(selectedPoint.carrier),
                flexShrink: 0,
              }}
            />
            <span style={{ fontWeight: 'var(--fw-semibold)' }}>
              {carrierLabel(selectedPoint.carrier)}
            </span>
            <span style={{ color: 'var(--color-text-secondary)', overflowWrap: 'anywhere' }}>
              {selectedPoint.address ?? `ПВЗ ${selectedPoint.externalId}`}
            </span>
            <button
              type="button"
              className="btn btn-tertiary"
              style={{ fontSize: 'var(--fs-caption)', marginLeft: 'auto' }}
              onClick={() => {
                onSelect(null);
                onPickupSelect?.(null);
              }}
            >
              Сбросить
            </button>
          </div>
          {selectedPoint.workingHours && (
            <div style={{ color: 'var(--color-text-secondary)' }}>
              График: {selectedPoint.workingHours}
            </div>
          )}
          {(selectedPoint.maxWeightG !== null || selectedPoint.storagePeriodDays !== null) && (
            <div style={{ color: 'var(--color-text-secondary)' }}>
              {[
                selectedPoint.maxWeightG ? `до ${Math.round(selectedPoint.maxWeightG / 1000)} кг` : null,
                selectedPoint.storagePeriodDays ? `хранение ${selectedPoint.storagePeriodDays} дн.` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          )}
          {selectedPoint.carrier === 'pickup' ? (
            <div style={{ fontWeight: 'var(--fw-semibold)' }}>
              Самовывоз у исполнителя · 0 ₽ — при получении
            </div>
          ) : quoteBusy ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
              <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
              <span style={{ color: 'var(--color-text-secondary)' }}>Считаем доставку…</span>
            </div>
          ) : quote?.success && quote.priceRub !== null ? (
            <div style={{ display: 'grid', gap: 2 }}>
              <span style={{ fontWeight: 'var(--fw-semibold)' }}>
                {`≈ ${Math.round(quote.priceRub).toLocaleString('ru-RU')} ₽`}
                {quote.minDays !== null
                  ? ` · ${quote.minDays}${quote.maxDays !== null && quote.maxDays !== quote.minDays ? `–${quote.maxDays}` : ''} дн.`
                  : ''}
              </span>
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {`посылка ${Math.round(quote.weightGrams)} г · ${quote.lengthCm}×${quote.widthCm}×${quote.heightCm} см · ценность ${Math.round(quote.declaredValueRub).toLocaleString('ru-RU')} ₽ (с запасом на упаковку)`}
              </span>
            </div>
          ) : (
            <div style={{ color: 'var(--color-text-secondary)' }}>
              {quote?.error ?? 'Тариф пока не рассчитан — оформится без предоплаты доставки.'}
            </div>
          )}
        </div>
      )}

      {/* The selection table stays ONLY as the no-map fallback (Артем). */}
      {showFallbackList && points !== null && (
        <>
          {filtered.length === 0 ? (
            <p style={{ color: 'var(--color-text-secondary)' }}>
              {city ? `В «${city}» пунктов нет — попробуйте другой город или «Все города».` : 'Пункты не найдены.'}
            </p>
          ) : (
            <div style={{ display: 'grid', gap: 'var(--space-2xs)', maxHeight: 260, overflowY: 'auto' }}>
              {filtered.slice(0, 100).map((p) => {
                const active =
                  (selected?.carrier === p.carrier && selected?.externalId === p.externalId)
                  || (p.carrier === 'pickup' && selectedPickupId === p.externalId);
                return (
                  <button
                    key={`${p.carrier}:${p.externalId}`}
                    type="button"
                    aria-pressed={active}
                    onClick={() => {
                      if (p.carrier === 'pickup') onPickupSelect?.(p.externalId);
                      else onSelect(toSelection(p));
                    }}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--space-2xs) var(--space-sm)',
                      borderRadius: 'var(--radius-md)',
                      border: active
                        ? '2px solid var(--color-primary)'
                        : '1px solid var(--color-separator)',
                      background: 'var(--color-surface)',
                      color: active ? 'var(--color-primary)' : 'var(--color-text-primary)',
                      fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                      cursor: 'pointer',
                    }}
                  >
                    {pointLabel(p)}
                    {p.workingHours ? ` · ${p.workingHours}` : ''}
                  </button>
                );
              })}
              {filtered.length > 100 && (
                <p style={{ color: 'var(--color-text-secondary)' }}>
                  Показаны первые 100 из {filtered.length} — уточните город.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
